import React, { useState } from "react";
import Modal from "react-modal";
import { Button } from "../../Button";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";
import { deleteSpecialPartner, SpecialPartnerViewModel } from "../../../api/specialPartners";

interface DeleteSpecialPartnerModalProps {
  isOpen: boolean;
  partner: SpecialPartnerViewModel | null;
  onClose: () => void;
  onSuccess: (partnerName: string) => void;
}

const DeleteSpecialPartnerModal: React.FC<DeleteSpecialPartnerModalProps> = ({
  isOpen,
  partner,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!partner) return;
    setLoading(true);
    setApiError(null);
    try {
      await deleteSpecialPartner({ id: partner.id });
      onSuccess(partner.campaignName);
    } catch (err: any) {
      setApiError(err?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setApiError(null);
    onClose();
  };

  if (!partner) return null;

  return (
    <Modal
      className="modal-content md:max-w-[420px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Delete Campaign"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">Delete Campaign</h2>
        <Button onClick={handleClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <p className="text-gray-700 mb-2">
        Are you sure you want to delete{" "}
        <span className="font-medium">{partner.campaignName}</span>?
      </p>
      <p className="text-sm text-gray-500 mb-4">
        This action cannot be undone.
      </p>

      {apiError && (
        <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg mb-4">
          {apiError}
        </div>
      )}

      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          isLoading={loading}
          className="bg-red-600 hover:bg-red-700 text-white border-red-600"
        >
          Delete
        </Button>
      </div>
    </Modal>
  );
};

export default DeleteSpecialPartnerModal;
